import {
  MarketplacePermissions,
  useLanguageCode,
  useMarketplacePermissions,
  useUserData,
} from 'hooks';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import marketplaceAction from 'redux/actions/marketplaceAction';
import { MARKETPLACE_TOKEN } from 'constants/index';
import { checkEnabledFeature, getKeyFeaturesConfig } from 'utils/function';

export type TMarketplaceContext = {
  marketplacePermissions?: MarketplacePermissions;
  isMarketplaceEnabled?: boolean;
  isSupportCountry?: boolean;
  isMarketplacePermissionLoading?: boolean;
  checkMarketplaceEnabledFeature?: (key: string) => boolean;
};

export const MarketplaceContext = createContext<TMarketplaceContext>({});

export const useMarketplaceProvider = (): TMarketplaceContext =>
  useContext(MarketplaceContext);

export const MarketplaceContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const languageCode = useLanguageCode();
  const dispatch = useDispatch();
  const currentUser = useUserData();
  const marketplacePermissions = useMarketplacePermissions();

  const { user: userMarketplace } = useSelector(
    (state: RootState) => state.marketplaceReducer,
  );
  const isSupportCountry = userMarketplace?.is_supported_country;

  const isMarketplaceEnabled = marketplacePermissions?.enabled;

  const checkMarketplaceEnabledFeature = (key: string) => {
    const marketplaceFeatures = marketplacePermissions?.features || [];
    const feature = getKeyFeaturesConfig(marketplaceFeatures, key);
    const isEnabled = isMarketplaceEnabled && checkEnabledFeature(feature);
    return isEnabled;
  };

  useEffect(() => {
    if (userMarketplace?.access_token_info) {
      localStorage.setItem(
        MARKETPLACE_TOKEN,
        JSON.stringify(userMarketplace?.access_token_info),
      );
    }
  }, [userMarketplace?.access_token_info]);

  useEffect(() => {
    if (currentUser?.email && isMarketplaceEnabled) {
      dispatch(marketplaceAction.getUser(currentUser.email));
    }
  }, [currentUser?.email, isMarketplaceEnabled]);

  useEffect(() => {
    const shouldDispatchToCallServices =
      userMarketplace?.id && isMarketplaceEnabled;

    if (shouldDispatchToCallServices) {
      if(isSupportCountry) {
        dispatch(
          marketplaceAction.getCategory({
            user_id: userMarketplace?.id,
            persona_id: currentUser.persona_definition || '',
          }),
        );
        dispatch(marketplaceAction.getCartDetails(userMarketplace.id, languageCode?.azure));
      } else {
        dispatch(marketplaceAction.getCategorySuccess([]));
      }
      dispatch(marketplaceAction.getSupportedCountries(userMarketplace.id));
    }
  }, [isMarketplaceEnabled, userMarketplace?.id, isSupportCountry]);

  const value = useMemo(() => {
    return {
      marketplacePermissions,
      isMarketplaceEnabled,
      isSupportCountry,
      isMarketplacePermissionLoading: marketplacePermissions?.enabled === undefined,
      checkMarketplaceEnabledFeature,
    };
  }, [marketplacePermissions, currentUser?.email, isSupportCountry]);

  return (
    <MarketplaceContext.Provider value={value}>
      {children}
    </MarketplaceContext.Provider>
  );
};
